import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { motion } from "motion/react";
import { ArrowRight, CheckCircle2, CreditCard, Lock, LogIn, ShieldCheck } from "lucide-react";
import { PasswordChangePrompt } from "../components/PasswordChangePrompt";
import {
  fetchPublicDraftOrderSuccess,
  setupPublicDraftOrderAccount,
  type PublicDraftOrderSuccess,
  WorkflowApiError,
} from "../services/workflow";
import { getStripePaymentStatusLabel } from "../services/workflow-ui";

function formatDateTime(value: string | null) {
  if (!value) {
    return null;
  }

  try {
    return new Intl.DateTimeFormat("de-DE", {
      dateStyle: "long",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return null;
  }
}

export function OrderSuccess() {
  const { shareSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [success, setSuccess] = useState<PublicDraftOrderSuccess | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isPasswordSaved, setIsPasswordSaved] = useState(false);

  const sessionId = searchParams.get("session_id");
  const hasPaymentIssue = success?.hasPaymentIssue === true;
  const isPaymentPending = success ? success.isPaymentConfirmed !== true : false;
  const acceptedAtLabel = formatDateTime(success?.orderAcceptedAt || null);
  const loginHref = useMemo(() => {
    if (!success?.customerEmail) {
      return "/login";
    }

    return `/login?email=${encodeURIComponent(success.customerEmail)}`;
  }, [success?.customerEmail]);
  const deferredSetupHref = useMemo(() => {
    if (!success?.customerEmail) {
      return "/login?setup=1";
    }

    return `/login?email=${encodeURIComponent(success.customerEmail)}&setup=1`;
  }, [success?.customerEmail]);

  useEffect(() => {
    let isMounted = true;

    const loadSuccess = async () => {
      if (!shareSlug || !sessionId) {
        if (isMounted) {
          setError("Die Erfolgsseite konnte nicht geladen werden, weil die Stripe-Session fehlt.");
          setIsLoading(false);
        }
        return;
      }

      try {
        const nextSuccess = await fetchPublicDraftOrderSuccess(shareSlug, sessionId);

        if (!isMounted) {
          return;
        }

        setSuccess(nextSuccess);
        setIsPasswordSaved(nextSuccess.mustSetPassword !== true);
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        setError(
          loadError instanceof WorkflowApiError
            ? loadError.message
            : "Die Zahlung konnte nicht verarbeitet werden",
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadSuccess();

    return () => {
      isMounted = false;
    };
  }, [sessionId, shareSlug]);

  useEffect(() => {
    if (!shareSlug || !sessionId || !success || success.isPaymentConfirmed || success.hasPaymentIssue) {
      return;
    }

    const retryTimer = window.setTimeout(async () => {
      try {
        const nextSuccess = await fetchPublicDraftOrderSuccess(shareSlug, sessionId);
        setSuccess(nextSuccess);
        setIsPasswordSaved(nextSuccess.mustSetPassword !== true);
      } catch {
        // Keep the current pending state visible and retry on the next cycle.
      }
    }, 3000);

    return () => {
      window.clearTimeout(retryTimer);
    };
  }, [sessionId, shareSlug, success]);

  const handlePasswordSetup = async (password: string) => {
    if (!shareSlug || !sessionId) {
      throw new Error("Stripe-Session fehlt");
    }

    const nextSuccess = await setupPublicDraftOrderAccount(shareSlug, {
      sessionId,
      password,
    });
    setSuccess(nextSuccess);
    setIsPasswordSaved(true);
  };

  return (
    <div className="pt-20">
      <section className="relative overflow-hidden bg-gradient-to-b from-gray-50 to-white">
        <div className="absolute inset-x-0 top-0 h-64 bg-[radial-gradient(circle_at_top,_rgba(0,0,0,0.07),_transparent_60%)]" />
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.05fr)_420px] lg:items-start">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-8"
            >
              <div className="inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-sm font-medium text-white">
                <CheckCircle2 size={16} />
                {hasPaymentIssue
                  ? "Zahlungsproblem"
                  : isPaymentPending
                    ? "Bestellung übernommen"
                    : "Zahlung erfolgreich"}
              </div>

              <div className="space-y-4">
                <h1 className="max-w-3xl text-4xl leading-tight text-gray-950 lg:text-6xl">
                  {hasPaymentIssue
                    ? "Bei der Zahlungsbestätigung ist ein Problem aufgetreten."
                    : "Ihr Auftrag ist eingegangen. Jetzt richten wir Ihren Zugang ein."}
                </h1>
                <p className="max-w-2xl text-lg leading-relaxed text-gray-600">
                  {hasPaymentIssue
                    ? success?.paymentIssueMessage ||
                      "Bitte kehren Sie zur Entwurfsvorschau zurück und starten Sie die Bestellung erneut."
                    : isPaymentPending
                    ? "Ihre Bestellung wurde bereits übernommen. Bei SEPA-Lastschrift meldet Stripe die endgültige Zahlungsbestätigung oft erst später. Ihr Zugang kann trotzdem schon eingerichtet werden."
                    : "Ihre Bestellung wurde erfolgreich übernommen. Auf dieser Seite vergeben Sie Ihr Passwort für den Kundenbereich und erfahren direkt, wie es danach weitergeht."}
                </p>
              </div>

              {acceptedAtLabel && !hasPaymentIssue ? (
                <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 shadow-sm">
                  <CreditCard size={16} />
                  {isPaymentPending ? "Bestellung übernommen am" : "Zahlung bestätigt am"} {acceptedAtLabel}
                </div>
              ) : null}

              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-950 text-white">
                    <CheckCircle2 size={22} />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-950">1. Auftrag bestätigt</h2>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">
                    {success?.projectTitle
                      ? `Ihr Projekt "${success.projectTitle}" ist jetzt verbindlich bei uns eingegangen.`
                      : "Ihr Projekt ist jetzt verbindlich bei uns eingegangen."}
                  </p>
                </div>

                <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-950 text-white">
                    <Lock size={22} />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-950">2. Zugang sichern</h2>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">
                    Vergeben Sie jetzt Ihr persönliches Passwort für den Kundenbereich. Danach ist
                    Ihr Zugang dauerhaft eingerichtet.
                  </p>
                </div>

                <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-950 text-white">
                    <ShieldCheck size={22} />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-950">3. Weiter geht's</h2>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">
                    Nach dem Login finden Sie dort künftig Projektstand, Abstimmungen und alle
                    nächsten Schritte gesammelt an einem Ort.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="rounded-[2rem] border border-gray-200 bg-white p-7 shadow-xl shadow-gray-200/70"
            >
              {isLoading ? (
                <div className="space-y-4">
                  <div className="h-4 w-32 rounded-full bg-gray-100" />
                  <div className="h-10 w-full rounded-2xl bg-gray-100" />
                  <div className="h-28 w-full rounded-3xl bg-gray-100" />
                </div>
              ) : error ? (
                <div className="space-y-5">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-red-600">
                      Fehler
                    </p>
                    <h2 className="mt-3 text-2xl font-semibold text-gray-950">
                      Erfolgsseite konnte nicht geladen werden
                    </h2>
                  </div>
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm leading-relaxed text-red-700">
                    {error}
                  </div>
                  <div className="flex flex-col gap-3">
                    <Link
                      to={shareSlug ? `/preview/${shareSlug}` : "/"}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-white transition hover:bg-gray-800"
                    >
                      Zurück zur Vorschau
                    </Link>
                    <Link
                      to="/"
                      className="inline-flex items-center justify-center rounded-2xl border border-gray-300 px-5 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                    >
                      Zur Startseite
                    </Link>
                  </div>
                </div>
              ) : success ? (
                <div className="space-y-6">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-gray-500">
                      Ihr Zugang
                    </p>
                    <h2 className="mt-3 text-2xl font-semibold text-gray-950">
                      {success.mustSetPassword && !isPasswordSaved
                        ? "Passwort vergeben"
                        : "Kundenbereich öffnen"}
                    </h2>
                    <p className="mt-3 text-sm leading-relaxed text-gray-600">
                      {success.customerName ? `Für ${success.customerName}` : "Für Ihr Kundenkonto"}
                      {" · "}
                      {success.customerEmail}
                    </p>
                  </div>

                  {hasPaymentIssue ? (
                    <div className="space-y-5">
                      <div className="rounded-3xl border border-red-200 bg-red-50 p-5">
                        <div className="flex items-start gap-3">
                          <CreditCard className="mt-0.5 text-red-700" size={22} />
                          <div>
                            <h3 className="text-base font-semibold text-red-900">
                              Zahlung konnte nicht bestätigt werden
                            </h3>
                            <p className="mt-2 text-sm leading-relaxed text-red-800">
                              {success.paymentIssueMessage ||
                                "Bei der Zahlungsbestätigung gab es ein Problem. Bitte starten Sie die Bestellung erneut."}
                            </p>
                            {success.paymentStatus ? (
                              <p className="mt-2 text-xs font-medium uppercase tracking-[0.14em] text-red-700">
                                Zahlungsstatus: {getStripePaymentStatusLabel(success.paymentStatus)}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <Link
                        to={shareSlug ? `/preview/${shareSlug}` : "/"}
                        className="inline-flex w-full items-center justify-center rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-white transition hover:bg-gray-800"
                      >
                        Zurück zur Vorschau
                      </Link>
                    </div>
                  ) : isPaymentPending ? (
                    <div className="space-y-5">
                      <div className="rounded-3xl border border-blue-200 bg-blue-50 p-5">
                        <div className="flex items-start gap-3">
                          <CreditCard className="mt-0.5 text-blue-700" size={22} />
                          <div>
                            <h3 className="text-base font-semibold text-blue-900">
                              Zahlung wird noch verarbeitet
                            </h3>
                            <p className="mt-2 text-sm leading-relaxed text-blue-800">
                              Der Checkout ist abgeschlossen und Ihr Auftrag wurde übernommen. Der
                              tatsächliche Zahlungseingang wird von Stripe bei SEPA-Lastschrift
                              erst verzögert bestätigt. Die Seite aktualisiert diesen Hinweis
                              automatisch.
                            </p>
                            {success.paymentStatus ? (
                              <p className="mt-2 text-xs font-medium uppercase tracking-[0.14em] text-blue-700">
                                Zahlungsstatus: {getStripePaymentStatusLabel(success.paymentStatus)}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {!hasPaymentIssue && success.mustSetPassword && !isPasswordSaved ? (
                    <div className="space-y-5">
                      <PasswordChangePrompt
                        title="Konto aktivieren"
                        description="Damit Ihr Kundenkonto sicher eingerichtet ist, wählen Sie bitte jetzt Ihr persönliches Passwort."
                        submitLabel="Passwort festlegen"
                        onSubmitPassword={handlePasswordSetup}
                        onSuccess={() => setIsPasswordSaved(true)}
                      />

                      <div className="rounded-3xl border border-gray-200 bg-gray-50 p-5 text-sm leading-relaxed text-gray-600">
                        Falls Sie das Passwort nicht sofort vergeben möchten, können Sie später
                        jederzeit über einen E-Mail-Link fortfahren.
                        <Link
                          to={deferredSetupHref}
                          className="mt-3 inline-flex items-center gap-2 font-medium text-gray-900 hover:text-black"
                        >
                          Link später per E-Mail anfordern
                        </Link>
                      </div>
                    </div>
                  ) : !hasPaymentIssue ? (
                    <div className="space-y-5">
                      <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="mt-0.5 text-emerald-700" size={22} />
                          <div>
                            <h3 className="text-base font-semibold text-emerald-900">
                              Zugang bereit
                            </h3>
                            <p className="mt-2 text-sm leading-relaxed text-emerald-800">
                              Ihr Passwort ist gesetzt. Sie können sich jetzt mit Ihrer E-Mail und
                              Ihrem neuen Passwort einloggen.
                            </p>
                          </div>
                        </div>
                      </div>

                      <Link
                        to={loginHref}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-white transition hover:bg-gray-800"
                      >
                        <LogIn size={16} />
                        Jetzt einloggen
                      </Link>
                    </div>
                  ) : null}

                  {!hasPaymentIssue ? (
                    <div className="rounded-3xl border border-gray-200 bg-gray-50 p-5 text-sm leading-relaxed text-gray-600">
                      Nach dem Login sehen Sie Ihren aktuellen Projektstatus und können alle weiteren
                      Abstimmungen zentral im Kundenbereich verfolgen.
                    </div>
                  ) : null}
                </div>
              ) : null}
            </motion.div>
          </div>

          <div className="mt-12 flex flex-wrap gap-4">
            <Link
              to="/ablauf"
              className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-5 py-3 text-sm font-medium text-gray-900 transition hover:bg-gray-100"
            >
              So geht es weiter
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium text-gray-600 transition hover:text-black"
            >
              Zur Startseite
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
